import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import styles from '../assets/editRoomModal.module.css';

const EditRoomModal = ({ isOpen, onClose, room, propertyId }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    key_number: '',
    head_guardian: false,
    is_couple: false,
    exclude_display: false,
    type_id: 1
  });
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load room types
  useEffect(() => {
    const loadRoomTypes = async () => {
      try {
        // Backend route: /api/room-types
        const response = await api.get('/room-types');
        setRoomTypes(response.data?.data || response.data || []);
      } catch (error) {
        console.error('Error loading room types:', error);
        // Fallback to mock data
        setRoomTypes([
          { id: 1, name: 'Guardian' },
          { id: 2, name: 'Maintenance/Other' }
        ]);
      }
    };

    if (isOpen) {
      loadRoomTypes();
    }
  }, [isOpen]);

  // Populate form when room data changes
  useEffect(() => {
    if (room) {
      setFormData({
        name: room.name || '',
        price: room.price || '',
        key_number: room.key_number || '',
        head_guardian: room.head_guardian || false,
        is_couple: room.is_couple || false,
        exclude_display: room.exclude_display || false,
        type_id: room.type_id || 1
      });
    }
  }, [room]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Room name is required');
      return;
    }

    setLoading(true);
    try {
      // Backend route: /api/rooms/{roomId}
      const response = await api.put(`/rooms/${room.id}`, formData);
      
      if (response.data?.success !== false) {
        toast.success('Room updated successfully');
        onClose();
      } else {
        toast.error(response.data?.message || 'Failed to update room');
      }
    } catch (error) {
      console.error('Error updating room:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update room';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']} onClick={onClose}>
      <div className={styles['edit-room-modal']} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles['modal-header']}>
          <h4 className={styles['modal-title']}>Edit room of property</h4>
          <button 
            type="button" 
            className={styles['btn-modal-close']} 
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles['modal-body']}>
          <form id="edit-room-form" className={styles['modal-form']} onSubmit={handleSubmit}>
            <input type="hidden" name="property_id" id="field_property_id" value={propertyId} />
            
            <fieldset className={styles['form-horizontal']}>
              {/* Room Name */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']} ${styles['label-required']}`}>
                  Number / Name
                </label>
                <div className={styles['col-sm-5']}>
                  <input
                    type="text"
                    name="name"
                    id="field_name"
                    className={styles['form-control']}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Price */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']}`}>
                  Price
                </label>
                <div className={styles['col-sm-5']}>
                  <input
                    type="text"
                    name="price"
                    id="field_price"
                    className={styles['form-control']}
                    value={formData.price}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Key Number */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']}`}>
                  Number of keys
                </label>
                <div className={styles['col-sm-5']}>
                  <input
                    type="text"
                    name="key_number"
                    id="field_key_number"
                    className={styles['form-control']}
                    value={formData.key_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Head Guardian */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']}`}>
                  Head Guardian
                </label>
                <div className={styles['col-sm-5']}>
                  <input
                    type="checkbox"
                    name="head_guardian"
                    id="head_guardian"
                    checked={formData.head_guardian}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Is for couple? */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']}`}>
                  Is for couple?
                </label>
                <div className={styles['col-sm-5']}>
                  <input
                    type="checkbox"
                    name="is_couple"
                    id="is_couple"
                    checked={formData.is_couple}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Exclude from display */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']}`}>
                  Exclude from display on the website
                </label>
                <div className={styles['col-sm-5']}>
                  <input
                    type="checkbox"
                    name="exclude_display"
                    id="exclude_display"
                    checked={formData.exclude_display}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Room Type */}
              <div className={styles['form-group']}>
                <label className={`${styles['control-label']} ${styles['col-sm-4']} ${styles['label-required']}`}>
                  Type
                </label>
                <div className={styles['col-sm-5']}>
                  <select
                    name="type_id"
                    className={styles['form-control']}
                    value={formData.type_id}
                    onChange={handleInputChange}
                    required
                  >
                    {roomTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>

            <fieldset className={styles['form-horizontal']}>
              <div className={styles['form-group']}>
                <div className={styles['col-sm-4']}></div>
                <div className={styles['col-sm-5']}>
                  <button
                    type="submit"
                    className={`${styles['btn']} ${styles['btn-head']} ${styles['btn-standard']} ${styles['modal-submit-button']}`}
                    disabled={loading}
                  >
                    <i className="glyphicon glyphicon-ok"></i> 
                    {loading ? 'Saving...' : 'Save Room'}
                  </button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>

        {/* Modal Footer */}
        <div className={styles['modal-footer']}>
          <button 
            type="button" 
            className={`${styles['btn']} ${styles['btn-default']} ${styles['btn-modal-close']}`} 
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRoomModal;
